import { useEffect, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  MenuItem,
  Autocomplete,
  Select,
  InputLabel,
  Chip,
  useTheme,
  CircularProgress,
  FormControl,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Resource, CreateResourceDTO } from '../../types/resource';
import { TIMEZONES } from '../../types/timezone';
import { useWorkingHours } from '../../hooks/workingHours/useWorkingHours';
import { useServices } from '../../hooks/service/useServices';
import { Service } from '../../types/service';
import { convertTo24HourFormat } from '../../utils/timeUtils';
import { DAYS_OF_WEEK, TimeSlot } from '../../types/workingHours';
import DayTimeSlot from '../settings/WorkingHours/DayTimeSlot';
import { AUTH_CONSTANTS, ROLES } from '../../config/constants';

interface ResourceFormProps {
  resource?: Resource | null;
  isEdit: boolean;
  onSubmit: (data: CreateResourceDTO) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

export default function ResourceForm({
  resource,
  isEdit,
  onSubmit,
  onClose,
  isSubmitting,
}: ResourceFormProps) {
  const theme = useTheme();
  const { services, isLoading } = useServices();
  const { settingsOrgWorkingHours } = useWorkingHours();
  const [useOrganizationHours, setUseOrganizationHours] = useState(!isEdit);
  const [workingHours, setWorkingHours] = useState<Record<string, TimeSlot[]>>();

  const [availableServices, setAvailableServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<
    {
      id: number;
      isSelected: boolean;
      isRemoved: boolean;
      isModified: boolean;
      mappingId?: number;
    }[]
  >([]);

  const serviceIds = resource?.serviceResourceMapping?.map(mapping => mapping.serviceId) || [];

  const resourceWorkingHours =
    resource?.workingHours?.map(wh => ({
      ...wh,
      day: wh.day.charAt(0).toUpperCase() + wh.day.slice(1),
      startTime: convertTo24HourFormat(wh.startTime),
      endTime: convertTo24HourFormat(wh.endTime),
    })) || [];
  const { control, handleSubmit, watch } = useForm<CreateResourceDTO>({
    defaultValues: resource
      ? {
          id: resource.id,
          name: resource.name,
          description: resource.description || undefined,
          defaultDuration: resource.defaultDuration || 30,
          bookingsPerSession: resource.bookingsPerSession || 1,
          phone: resource.phone,
          email: resource.email,
          type: resource.type || undefined,
          isDashboardEnabled: resource.isDashboardEnabled,
          userRole: resource.userRole,
          services: serviceIds,
          timezone: resource.timezone,
          useOrganizationHours: true,
          workingHours: resourceWorkingHours,
        }
      : {
          name: '',
          defaultDuration: 30,
          bookingsPerSession: 1,
          phone: '',
          email: '',
          type: '',
          userRole: AUTH_CONSTANTS.ROLES.USER,
          isDashboardEnabled: true,
          timezone: 'Australia/Sydney',
          useOrganizationHours: true,
        },
  });

  const isDashboardEnabled = watch('isDashboardEnabled');

  const handleFormSubmit = (data: CreateResourceDTO) => {
    const servicesWithStatus = selectedService.map(service => ({
      serviceId: service.id,
      isSelected: service.isSelected,
      isRemoved: service.isRemoved,
      isModified: service.isModified,
      mappingId: service.mappingId,
    }));

    const formattedWorkingHours = Object.entries(workingHours || {}).flatMap(([day, slots]) =>
      slots.map(slot => ({
        id: slot.id,
        day: day.toLowerCase(),
        startTime: slot.startTime,
        endTime: slot.endTime,
        enabled: slot.enabled,
        isModified: slot.isModified,
        orgId: '',
      }))
    );

    onSubmit({
      ...data,
      services: servicesWithStatus,
      workingHours: formattedWorkingHours,
    });
  };

  useEffect(() => {
    if (services) {
      setAvailableServices(services);
    }
  }, [services]);

  useEffect(() => {
    if (isEdit && resource?.workingHours) {
      const parsedWorkingHours: Record<string, TimeSlot[]> = resource.workingHours.reduce(
        (acc, wh) => {
          const day = wh.day.charAt(0).toUpperCase() + wh.day.slice(1).toLowerCase(); // Ensure proper capitalization
          if (!acc[day]) {
            acc[day] = [];
          }
          acc[day].push({
            startTime: convertTo24HourFormat(wh.startTime),
            endTime: convertTo24HourFormat(wh.endTime),
            enabled: true, // Assuming all time slots should be enabled
            id: wh.id,
            isModified: false,
          });
          acc[day].sort((a, b) => (a.startTime > b.startTime ? 1 : -1)); // Sort by start time
          return acc;
        },
        {} as Record<string, TimeSlot[]>
      );

      setWorkingHours(parsedWorkingHours);
    } else if (!isEdit && useOrganizationHours && settingsOrgWorkingHours) {
      const parsedWorkingHours: Record<string, TimeSlot[]> = Object.keys(
        settingsOrgWorkingHours
      ).reduce(
        (acc, day) => {
          const formattedDay = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase(); // Ensure correct format
          acc[formattedDay] = settingsOrgWorkingHours[day]
            .map((slot: any) => ({
              startTime: slot.startTime,
              endTime: slot.endTime,
              enabled: slot.enabled ?? false, // Default to false if missing
              id: slot.id,
              isModified: slot.isModified || false,
            }))
            .sort((a, b) => (a.startTime > b.startTime ? 1 : -1)); // Sort by start time
          return acc;
        },
        {} as Record<string, TimeSlot[]>
      );

      setWorkingHours(parsedWorkingHours);
    }
  }, [isEdit, resource, useOrganizationHours, settingsOrgWorkingHours]);

  useEffect(() => {
    if (isEdit && resource?.serviceResourceMapping) {
      const initialServices = resource.serviceResourceMapping.map(mapping => ({
        id: mapping.serviceId,
        isSelected: true,
        isRemoved: false,
        isModified: false,
        mappingId: mapping.id,
      }));
      setSelectedService(initialServices);
    }
  }, [isEdit, resource]);

  const handleToggleDay = (day: string, enabled: boolean) => {
    setWorkingHours(prev => {
      if (!prev) return prev;

      const updatedDay = enabled
        ? [{ startTime: '09:00', endTime: '17:00', enabled: true, isModified: false }]
        : (prev[day] || []).map(slot => ({ ...slot, enabled: false, isModified: true }));

      return { ...prev, [day]: updatedDay };
    });
  };

  const handleTimeChange = (day: string, index: number, updates: Partial<TimeSlot>) => {
    setWorkingHours(prevHours => {
      const newState = {
        ...prevHours,
        [day]: (prevHours?.[day] ?? []).map((slot, i) =>
          i === index ? { ...slot, ...updates, isModified: slot.id ? true : slot.isModified } : slot
        ),
      };

      if (index >= newState[day].length) {
        newState[day].push({
          startTime: updates.startTime || '',
          endTime: updates.endTime || '',
          enabled: updates.enabled,
          isModified: updates.isModified || false,
          id: undefined,
        });
      }

      return newState;
    });
  };

  const handleDeleteSlot = (day: string, index: number) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: (prev?.[day] ?? []).map((slot, i) =>
        i === index ? { ...slot, enabled: false, isModified: true } : slot
      ),
    }));
  };

  if (isLoading || !workingHours || !services) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <DialogTitle>{resource ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Name"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Description" multiline rows={3} fullWidth />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="isDashboardEnabled"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Provide Access Login
                      </Typography>
                    }
                    sx={{ ml: 0 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 6 }}
                  >
                    Allow this resource to login and manage their own bookings
                  </Typography>
                </Box>
              )}
            />
          </Grid>

          {isDashboardEnabled && (
            <Grid item xs={12}>
              <Controller
                name="userRole"
                control={control}
                rules={{ required: 'Role is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    select
                    label="User Role"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  >
                    {ROLES.map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          )}

          {/* <Grid item xs={12}>
            <Divider />
          </Grid> */}

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Contact Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Email"
                      fullWidth
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Phone (Optional)" fullWidth size="small" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* <Grid item xs={12}>
            <Divider />
          </Grid> */}

          {/* Schedule Settings */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Schedule Settings
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="timezone"
                  control={control}
                  rules={{ required: 'Timezone is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                      {...field}
                      options={TIMEZONES}
                      getOptionLabel={option => option}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Timezone"
                          fullWidth
                          size="small"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                      onChange={(_, value) => field.onChange(value)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="defaultDuration"
                  control={control}
                  rules={{
                    required: 'Default Duration is required',
                    validate: value => {
                      const num = Number(value);
                      if (isNaN(num)) return 'Please enter a valid number';
                      if (num <= 0) return 'Duration must be greater than 0';
                      if (num <= 10) return 'Duration must be greater than 10 ';
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Default Duration (mins)"
                      fullWidth
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="bookingsPerSession"
                  control={control}
                  rules={{
                    required: 'Bookings per session is required',
                    validate: value => {
                      const num = Number(value);
                      if (isNaN(num)) return 'Please enter a valid number';
                      if (num <= 0) return 'Must be at least 1';
                      if (!Number.isInteger(num)) return 'Must be a whole number';
                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Bookings per Session"
                      fullWidth
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{ inputProps: { min: 1 } }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {availableServices.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                  Services
                </Typography>
                <Controller
                  name="services"
                  control={control}
                  render={({}) => (
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="select-services-label">Select Services</InputLabel>

                      <Select
                        labelId="select-services-label"
                        multiple
                        value={selectedService
                          .filter(service => service.isSelected)
                          .map(service => service.id)}
                        onChange={e => {
                          const selectedValues = e.target.value as number[];
                          setSelectedService(prevState => {
                            const currentIds = new Set(prevState.map(service => service.id));

                            // Update existing services
                            const result = [...prevState].map(service => {
                              const wasSelected = service.isSelected;
                              const isNowSelected = selectedValues.includes(service.id);

                              return {
                                ...service,
                                isSelected: isNowSelected,
                                isRemoved: !isNowSelected,
                                isModified: wasSelected !== isNowSelected || service.isModified, // Track if selection state changed
                              };
                            });

                            // Add new services
                            selectedValues.forEach(id => {
                              if (!currentIds.has(id)) {
                                result.push({
                                  id,
                                  isSelected: true,
                                  isRemoved: false,
                                  isModified: true, // New services are always modified
                                });
                              }
                            });

                            return result;
                          });
                        }}
                        label="Select Services"
                        renderValue={selected =>
                          selected
                            .map(
                              serviceId =>
                                availableServices.find(service => service.id === serviceId)?.name
                            )
                            .join(', ')
                        }
                      >
                        {availableServices.map(service => (
                          <MenuItem key={service.id} value={service.id}>
                            <Box sx={{ py: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body1">{service.name}</Typography>
                                <Chip
                                  label={`${service.duration} min`}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </>
          )}

          {/* <Grid item xs={12}>
            <Divider />
          </Grid> */}

          {/* Working Hours */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>
              Working Hours
            </Typography>

            {(workingHours?.length || !isEdit) && (
              <Box sx={{ mb: 2 }}>
                {!isEdit && (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useOrganizationHours}
                          onChange={e => setUseOrganizationHours(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          Use Organization Working Hours
                        </Typography>
                      }
                      sx={{ ml: 0 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', ml: 6 }}
                    >
                      Apply the default organization working hours to this resource
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {!useOrganizationHours &&
              DAYS_OF_WEEK.map(day => (
                <DayTimeSlot
                  key={day}
                  day={day}
                  timeSlot={workingHours[day]?.filter(slot => slot.enabled) || []}
                  onChange={handleTimeChange}
                  onDeleteSlot={(day, index) => {
                    handleDeleteSlot(day, index);
                  }}
                  onToggleDay={handleToggleDay}
                />
              ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {resource ? 'Update Staff Member' : 'Add Staff Member'}
        </Button>
      </DialogActions>
    </form>
  );
}
